import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Edit, Save, X, Send, History, Sparkles, Clock } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { wellnessService } from "@/services/wellnessService";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";
import { enUS } from "date-fns/locale";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

type DailyReflectionCardProps = {
  onSubmit?: (response: string) => void;
};

interface DailyReflection {
  id: string;
  date: string;
  reflection: string;
  mood: number;
  created_at: string;
  // Additional fields to preserve during updates
  energy?: number;
  focus?: number;
  motivation?: number;
  tasks?: string[];
  blockers?: string;
  wins?: string;
  helpNeeded?: boolean;
  helpWith?: string;
}

const DailyReflectionCard = ({ onSubmit }: DailyReflectionCardProps) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  
  const [todayReflection, setTodayReflection] = useState<DailyReflection | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedReflection, setEditedReflection] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  
  // Quick reflection state
  const [showQuickReflect, setShowQuickReflect] = useState(false);
  const [quickReflection, setQuickReflection] = useState("");
  const [isQuickSaving, setIsQuickSaving] = useState(false);
  
  // History modal state
  const [showHistory, setShowHistory] = useState(false);
  const [reflectionHistory, setReflectionHistory] = useState<DailyReflection[]>([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  
  // Prompts for quick reflection
  const quickPrompts = [
    "What made you smile today?",
    "How did you grow today?",
    "What are you grateful for?",
    "What challenged you most?",
    "What would you do differently?"
  ];
  const [selectedPrompt, setSelectedPrompt] = useState("");

  // Function to get mood label
  const getMoodLabel = (mood: number): string => {
    switch (mood) {
      case 1: return "low";
      case 2: return "okay";
      case 3: return "okay";
      case 4: return "good";
      case 5: return "great";
      default: return "okay";
    }
  };

  // Function to get mood variant for badge
  const getMoodVariant = (mood: number): "default" | "secondary" | "outline" | "destructive" => {
    switch (mood) {
      case 1:
      case 2:
        return "destructive";
      case 3:
        return "outline";
      case 4:
        return "secondary";
      case 5:
        return "default";
      default:
        return "outline";
    }
  };

  // Function to format date
  const formatDate = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      return format(date, "MMM dd, yyyy", { locale: enUS });
    } catch {
      return dateString;
    }
  };

  // Fetch today's reflection
  const fetchTodayReflection = async () => {
    if (!user?.id) {
      setIsLoading(false);
      return;
    }

    try {
      const checkIn = await wellnessService.getTodaysCheckIn();

      if (checkIn && checkIn.reflection) {
        setTodayReflection({
          id: checkIn.id,
          date: checkIn.date,
          reflection: checkIn.reflection,
          mood: checkIn.mood,
          created_at: checkIn.createdAt || checkIn.created_at,
          // Preserve all other fields for updates
          energy: checkIn.energy,
          focus: checkIn.focus,
          motivation: checkIn.motivation,
          tasks: checkIn.tasks,
          blockers: checkIn.blockers,
          wins: checkIn.wins,
          helpNeeded: checkIn.helpNeeded,
          helpWith: checkIn.helpWith,
        });
        setEditedReflection(checkIn.reflection);
      }
    } catch (error) {
      console.error("Error fetching today's reflection:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Save edited reflection
  const saveReflection = async () => {
    if (!user?.id || !todayReflection) return;

    setIsSaving(true);
    try {
      // Send all existing check-in fields to preserve data during update
      await wellnessService.createCheckIn({
        date: todayReflection.date,
        reflection: editedReflection,
        mood: todayReflection.mood,
        energy: todayReflection.energy ?? 50,
        focus: todayReflection.focus,
        motivation: todayReflection.motivation,
        tasks: todayReflection.tasks,
        blockers: todayReflection.blockers,
        wins: todayReflection.wins,
        helpNeeded: todayReflection.helpNeeded,
        helpWith: todayReflection.helpWith,
      });

      setTodayReflection({
        ...todayReflection,
        reflection: editedReflection
      });
      setIsEditing(false);
      
      toast({
        title: "Reflection updated",
        description: "Your daily reflection has been updated successfully.",
      });

      if (onSubmit) {
        onSubmit(editedReflection);
      }
    } catch (error) {
      console.error("Error saving reflection:", error);
      toast({
        title: "Error",
        description: "Failed to save your reflection",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Cancel editing
  const cancelEdit = () => {
    setEditedReflection(todayReflection?.reflection || "");
    setIsEditing(false);
  };

  // Start editing
  const startEdit = () => {
    setIsEditing(true);
  };

  // Navigate to check-in page
  const goToCheckIn = () => {
    navigate("/check-in");
  };

  // Save quick reflection
  const saveQuickReflection = async () => {
    if (!user?.id || !quickReflection.trim()) return;

    setIsQuickSaving(true);
    try {
      const today = new Date().toISOString().split("T")[0];
      
      // Get today's check-in - service returns data directly, not { success, data }
      const existingCheckIn = await wellnessService.getTodaysCheckIn().catch(() => null);

      if (existingCheckIn) {
        // Update existing check-in with reflection
        const combinedReflection = existingCheckIn.reflection 
          ? `${existingCheckIn.reflection}\n\n--- Quick Reflection ---\n${quickReflection}`
          : quickReflection;
          
        await wellnessService.createCheckIn({
          date: today,
          reflection: combinedReflection,
          mood: existingCheckIn.mood,
          energy: existingCheckIn.energy,
          focus: existingCheckIn.focus,
        });
      } else {
        // Create new minimal check-in with just reflection
        await wellnessService.createCheckIn({
          date: today,
          reflection: quickReflection,
          mood: 3, // neutral default
          energy: 50,
          focus: 50,
          motivation: 50
        });
      }

      setQuickReflection("");
      setShowQuickReflect(false);
      fetchTodayReflection(); // Refresh the data
      
      toast({
        title: "Quick reflection saved",
        description: "Your reflection has been captured for today.",
      });

      if (onSubmit) {
        onSubmit(quickReflection);
      }
    } catch (error) {
      console.error("Error saving quick reflection:", error);
      toast({
        title: "Error",
        description: "Failed to save your quick reflection",
        variant: "destructive",
      });
    } finally {
      setIsQuickSaving(false);
    }
  };

  // Fetch reflection history
  const fetchReflectionHistory = async () => {
    if (!user?.id) return;

    setIsLoadingHistory(true);
    try {
      // getCheckIns returns an array directly
      const checkIns = await wellnessService.getCheckIns({ limit: 10 });

      const reflections = (Array.isArray(checkIns) ? checkIns : [])
        .filter((ci: any) => ci.reflection)
        .map((ci: any) => ({
          id: ci.id,
          date: ci.date,
          reflection: ci.reflection,
          mood: ci.mood,
          created_at: ci.createdAt || ci.created_at,
        }));
      setReflectionHistory(reflections);
    } catch (error) {
      console.error("Error fetching reflection history:", error);
      toast({
        title: "Error",
        description: "Failed to load reflection history",
        variant: "destructive",
      });
    } finally {
      setIsLoadingHistory(false);
    }
  };

  // Open history modal
  const openHistory = () => {
    setShowHistory(true);
    fetchReflectionHistory();
  };

  // Fetch reflection on mount
  useEffect(() => {
    fetchTodayReflection();
  }, [user?.id]);

  return (
    <>
      <Card className="daily-reflection-card">
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            Daily Reflection
            <div className="flex gap-2">
              {!todayReflection && !isLoading && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowQuickReflect(true)}
                >
                  <Sparkles className="h-4 w-4 mr-1" />
                  Quick Reflect
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={openHistory}
              >
                <History className="h-4 w-4 mr-1" />
                History
              </Button>
              {todayReflection && !isEditing && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={startEdit}
                >
                  <Edit className="h-4 w-4 mr-1" />
                  Edit
                </Button>
              )}
            </div>
          </CardTitle>
          <CardDescription>
            Capture quick thoughts or deep reflections to build self-awareness
          </CardDescription>
        </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="text-center py-8">
            <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full mx-auto mb-2"></div>
            <p className="text-muted-foreground">Loading today's reflection...</p>
          </div>
        ) : todayReflection ? (
          <div className="space-y-4">
            {/* Reflection header with date and mood */}
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">
                {formatDate(todayReflection.date)}
              </span>
              <Badge variant={getMoodVariant(todayReflection.mood)}>
                {getMoodLabel(todayReflection.mood)}
              </Badge>
            </div>

            {/* Reflection content */}
            {isEditing ? (
              <div className="space-y-3">
                <Textarea
                  value={editedReflection}
                  onChange={(e) => setEditedReflection(e.target.value)}
                  placeholder="Write your daily reflection..."
                  className="min-h-32 resize-none"
                />
                <div className="flex justify-end gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={cancelEdit}
                    disabled={isSaving}
                  >
                    <X className="h-4 w-4 mr-1" />
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    onClick={saveReflection}
                    disabled={isSaving || !editedReflection.trim()}
                  >
                    {isSaving ? (
                      <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-1" />
                    ) : (
                      <Save className="h-4 w-4 mr-1" />
                    )}
                    Save
                  </Button>
                </div>
              </div>
            ) : (
              <div className="bg-muted/30 p-4 rounded-lg border">
                <p className="text-muted-foreground whitespace-pre-wrap">
                  {todayReflection.reflection}
                </p>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-8">
            <div className="bg-muted/30 p-4 rounded-lg border">
              <p className="text-muted-foreground mb-4">
                No reflection for today yet. Complete your daily check-in to add one.
              </p>
              <Button variant="outline" size="sm" onClick={goToCheckIn}>
                <Send className="h-4 w-4 mr-1" />
                Go to Check-in
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>

    {/* Quick Reflection Modal */}
    <Dialog open={showQuickReflect} onOpenChange={setShowQuickReflect}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5" />
            Quick Reflection
          </DialogTitle>
          <DialogDescription>
            Capture a quick thought or feeling about your day
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4">
          {/* Quick prompts */}
          <div>
            <p className="text-sm font-medium mb-2">Choose a prompt to get started:</p>
            <div className="flex flex-wrap gap-2">
              {quickPrompts.map((prompt, index) => (
                <Button
                  key={index}
                  variant={selectedPrompt === prompt ? "default" : "outline"}
                  size="sm"
                  onClick={() => {
                    setSelectedPrompt(prompt);
                    setQuickReflection(`${prompt}\n\n`);
                  }}
                  className="text-xs"
                >
                  {prompt}
                </Button>
              ))}
            </div>
          </div>
          
          {/* Reflection textarea */}
          <div>
            <Textarea
              value={quickReflection}
              onChange={(e) => setQuickReflection(e.target.value)}
              placeholder="Write your reflection..."
              className="min-h-32 resize-none"
              autoFocus
            />
          </div>
          
          {/* Actions */}
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setShowQuickReflect(false);
                setQuickReflection("");
                setSelectedPrompt("");
              }}
              disabled={isQuickSaving}
            >
              Cancel
            </Button>
            <Button
              onClick={saveQuickReflection}
              disabled={isQuickSaving || !quickReflection.trim()}
            >
              {isQuickSaving ? (
                <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              Save Reflection
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>

    {/* History Modal */}
    <Dialog open={showHistory} onOpenChange={setShowHistory}>
      <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="h-5 w-5" />
            Reflection History
          </DialogTitle>
          <DialogDescription>
            Your past reflections and growth journey
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 overflow-y-auto max-h-[60vh]">
          {isLoadingHistory ? (
            <div className="text-center py-8">
              <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full mx-auto mb-2"></div>
              <p className="text-muted-foreground">Loading your reflections...</p>
            </div>
          ) : reflectionHistory.length > 0 ? (
            <div className="space-y-4">
              {reflectionHistory.map((reflection) => (
                <div key={reflection.id} className="border rounded-lg p-4 space-y-2">
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm font-medium">
                        {formatDate(reflection.date)}
                      </span>
                    </div>
                    <Badge variant={getMoodVariant(reflection.mood)}>
                      {getMoodLabel(reflection.mood)}
                    </Badge>
                  </div>
                  <div className="bg-muted/30 p-3 rounded border text-sm">
                    <p className="whitespace-pre-wrap text-muted-foreground">
                      {reflection.reflection.length > 150 
                        ? `${reflection.reflection.substring(0, 150)}...` 
                        : reflection.reflection}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <History className="h-12 w-12 text-muted-foreground mx-auto mb-2" />
              <p className="text-muted-foreground">No reflections found</p>
              <p className="text-sm text-muted-foreground">
                Start reflecting to build your history
              </p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
    </>
  );
};

export default DailyReflectionCard;
